/**
 * Key Reconstruction Service
 *
 * Reconstructs a full private key from threshold key shares using Lagrange interpolation.
 * This is the same math used in Shamir's Secret Sharing.
 *
 * Given 3+ shares from DKG nodes, combines them to reconstruct the original private key.
 * The private key can then be used to sign transactions locally.
 *
 * Supports:
 * - ECDSA (secp256k1) for EVM chains
 * - Ed25519 for Solana (via secp256k1-to-Ed25519 derivation)
 */

import { ethers } from 'ethers';
import { getChainType, evmHexToTronAddress } from '../utils/chainDetect';
import * as ed from '@noble/ed25519';
import { sha512 } from '@noble/hashes/sha512';
import bs58 from 'bs58';

// Configure SHA-512 for @noble/ed25519 v3 (required before any ed25519 operations)
// v3 uses ed.hashes.sha512 (not ed.etc.sha512Sync which was the v2 API)
ed.hashes.sha512 = (...m) => sha512(ed.etc.concatBytes(...m));

// secp256k1 curve order (for EVM)
const SECP256K1_N = BigInt('0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141');

/**
 * Compute modular inverse using extended Euclidean algorithm
 * Returns a^(-1) mod m
 */
function modInverse(a, m) {
    a = ((a % m) + m) % m;
    let [old_r, r] = [a, m];
    let [old_s, s] = [1n, 0n];

    while (r !== 0n) {
        const quotient = old_r / r;
        [old_r, r] = [r, old_r - quotient * r];
        [old_s, s] = [s, old_s - quotient * s];
    }

    if (old_r > 1n) {
        throw new Error('Modular inverse does not exist');
    }

    return ((old_s % m) + m) % m;
}

/**
 * Compute Lagrange coefficient for a share at x=0
 * λ_i(0) = Π_{j≠i} (0 - x_j) / (x_i - x_j)
 */
function computeLagrangeCoefficient(shares, i) {
    const xi = BigInt(shares[i].shareId);
    let numerator = 1n;
    let denominator = 1n;

    for (let j = 0; j < shares.length; j++) {
        if (i === j) continue;

        const xj = BigInt(shares[j].shareId);

        // numerator *= (0 - xj) = -xj
        let negXj = (-xj) % SECP256K1_N;
        if (negXj < 0n) negXj += SECP256K1_N;
        numerator = (numerator * negXj) % SECP256K1_N;

        // denominator *= (xi - xj)
        let diff = (xi - xj) % SECP256K1_N;
        if (diff < 0n) diff += SECP256K1_N;
        denominator = (denominator * diff) % SECP256K1_N;
    }

    // λ = numerator * denominator^(-1) mod n
    const denomInv = modInverse(denominator, SECP256K1_N);
    return (numerator * denomInv) % SECP256K1_N;
}

/**
 * Reconstruct private key from key shares using Lagrange interpolation at x=0
 *
 * @param {Array} keyshares - Key shares from DKG nodes
 *   Each share should have: { nodeId, shareId (decimal string), shareValue (hex) }
 * @param {number} threshold - Minimum shares needed (default: 3)
 * @returns {Object} - { privateKey: BigInt, privateKeyHex: string }
 */
function reconstructPrivateKey(keyshares, threshold = 3) {
    if (keyshares.length < threshold) {
        throw new Error(`Need at least ${threshold} shares, got ${keyshares.length}`);
    }

    console.log(`Reconstructing private key from ${keyshares.length} shares (using first ${threshold})`);

    // Use first 'threshold' shares
    const sharesToUse = keyshares.slice(0, threshold);

    // Validate shares
    for (const share of sharesToUse) {
        if (!share.shareId) {
            throw new Error(`Share from node ${share.nodeId} missing shareId`);
        }
        if (!share.shareValue) {
            throw new Error(`Share from node ${share.nodeId} missing shareValue`);
        }
    }

    // Lagrange interpolation at x=0
    let result = 0n;

    for (let i = 0; i < sharesToUse.length; i++) {
        const share = sharesToUse[i];

        // Parse share value (hex string with 0x prefix)
        let shareValueHex = share.shareValue;
        if (shareValueHex.startsWith('0x')) {
            shareValueHex = shareValueHex.slice(2);
        }
        const y_i = BigInt('0x' + shareValueHex);

        // Compute Lagrange coefficient
        const lambda = computeLagrangeCoefficient(sharesToUse, i);

        // Add contribution: y_i * λ_i
        const contribution = (y_i * lambda) % SECP256K1_N;
        result = (result + contribution) % SECP256K1_N;
    }

    // Ensure positive result
    result = (result + SECP256K1_N) % SECP256K1_N;

    // Convert to hex string (32 bytes, padded)
    const privateKeyHex = '0x' + result.toString(16).padStart(64, '0');

    console.log('Private key reconstructed successfully');

    return {
        privateKey: result,
        privateKeyHex: privateKeyHex,
    };
}

/**
 * Verify that a reconstructed private key matches the expected address
 *
 * @param {string} privateKeyHex - Private key as hex string
 * @param {string} expectedAddress - Expected Ethereum address
 * @returns {boolean} - True if address matches
 */
function verifyKeyMatchesAddress(privateKeyHex, expectedAddress) {
    const wallet = new ethers.Wallet(privateKeyHex);
    // For Tron addresses: the same 20 bytes are wrapped differently (Base58Check
    // with 0x41 prefix vs 0x hex). Encode our derived EVM address as Tron form
    // and string-compare.
    if (getChainType(expectedAddress) === 'tron') {
        try {
            const tronEquivalent = evmHexToTronAddress(wallet.address);
            return tronEquivalent === expectedAddress;
        } catch (_) {
            return false;
        }
    }
    return wallet.address.toLowerCase() === expectedAddress.toLowerCase();
}

/**
 * Convert hex string to Uint8Array
 */
function hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
    }
    return bytes;
}

/**
 * Detect if an address is a Solana address.
 *
 * Kept as a named export for backward-compat with `dkgSimpleWithdrawal.js`
 * and other consumers; delegates to the centralised getChainType() helper
 * so Tron `T...` addresses are NOT mis-classified as Solana.
 */
function isSolanaAddress(address) {
    if (!address || typeof address !== 'string') return false;
    return getChainType(address) === 'solana';
}

/**
 * Convert secp256k1 private key to Ed25519 seed
 * This matches the backend derivation: SHA256(compressed_secp256k1_pubkey || "ed25519")
 *
 * @param {string} secp256k1PrivKeyHex - secp256k1 private key as hex string
 * @returns {Promise<Uint8Array>} - 32-byte Ed25519 seed
 */
async function secp256k1ToEd25519Seed(secp256k1PrivKeyHex) {
    // Get the secp256k1 public key from private key
    const signingKey = new ethers.utils.SigningKey(secp256k1PrivKeyHex);
    const publicKey = signingKey.compressedPublicKey; // 33 bytes compressed

    // Convert to bytes
    let pubKeyHex = publicKey;
    if (pubKeyHex.startsWith('0x')) {
        pubKeyHex = pubKeyHex.slice(2);
    }
    const pubKeyBytes = hexToBytes(pubKeyHex);

    // Hash: SHA256(compressed_pubkey || "ed25519")
    const domainSeparator = new TextEncoder().encode('ed25519');
    const combined = new Uint8Array(pubKeyBytes.length + domainSeparator.length);
    combined.set(pubKeyBytes);
    combined.set(domainSeparator, pubKeyBytes.length);

    // Use SubtleCrypto for SHA256
    const hashBuffer = await crypto.subtle.digest('SHA-256', combined);
    return new Uint8Array(hashBuffer);
}

/**
 * Reconstruct Solana Ed25519 key from secp256k1 shares
 * The backend returns secp256k1 shares for both EVM and Solana.
 * For Solana, we reconstruct the secp256k1 key first, then convert to Ed25519.
 *
 * @param {Array} keyshares - secp256k1 key shares from DKG nodes
 * @param {string} expectedAddress - Expected Solana address (base58)
 * @param {number} threshold - Minimum shares needed (default: 3)
 * @returns {Promise<Object>} - { ed25519Seed, solanaAddress }
 */
async function reconstructSolanaKeyFromSecp256k1Shares(keyshares, expectedAddress, threshold = 3) {
    console.log(`Reconstructing Solana key from ${keyshares.length} secp256k1 shares`);

    // Helper to try a specific subset of shares
    async function tryReconstruct(shares) {
        const { privateKeyHex } = reconstructPrivateKey(shares, threshold);
        const ed25519Seed = await secp256k1ToEd25519Seed(privateKeyHex);
        const ed25519PubKey = await ed.getPublicKey(ed25519Seed);
        const solanaAddress = bs58.encode(ed25519PubKey);
        return { ed25519Seed, solanaAddress, privateKeyHex: '0x' + Buffer.from(ed25519Seed).toString('hex') };
    }

    // Step 1: Try default first-N shares
    const result = await tryReconstruct(keyshares);
    console.log(`Derived Solana address: ${result.solanaAddress}`);

    if (result.solanaAddress === expectedAddress) {
        return result;
    }

    // Step 2: Default shares failed — try all C(N, threshold) combinations
    // This handles the case where one node returned a corrupted share
    console.warn(`Default shares [${keyshares.slice(0, threshold).map(s => s.nodeId).join(',')}] failed Solana address check, trying alternative combinations...`);

    for (let a = 0; a < keyshares.length; a++) {
        for (let b = a + 1; b < keyshares.length; b++) {
            for (let c = b + 1; c < keyshares.length; c++) {
                if (a === 0 && b === 1 && c === 2) continue; // Already tried
                const subset = [keyshares[a], keyshares[b], keyshares[c]];
                try {
                    const altResult = await tryReconstruct(subset);
                    if (altResult.solanaAddress === expectedAddress) {
                        console.log(`Solana reconstruction succeeded with shares from nodes [${subset.map(s => s.nodeId).join(',')}]`);
                        return altResult;
                    }
                } catch (e) {
                    // Skip invalid combination
                }
            }
        }
    }

    throw new Error(
        `Solana address mismatch: expected ${expectedAddress}, got ${result.solanaAddress}. ` +
        `No valid ${threshold}-of-${keyshares.length} share combination produces the expected address.`
    );
}

export {
    reconstructPrivateKey,
    reconstructSolanaKeyFromSecp256k1Shares,
    verifyKeyMatchesAddress,
    isSolanaAddress,
};
